/**
 * AuditTimeline — the ONE shared "roadmap timeline" view of audit rows
 * (CLAUDE.md §18.6 — don't reinvent).  Used by BOTH the full-page Data Center
 * audit log (AuditTrailPage) AND the per-form <AuditTrailButton> popup, so the
 * timeline looks and behaves identically everywhere.
 *
 * Groups the rows by calendar day (local, via the shared fmtLocalDate helper),
 * newest-first, colour-coded by operation (green created · blue value-changed ·
 * red deleted).
 */
import { useMemo } from 'react';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';

/** Minimal audit-row shape the timeline needs (structurally shared by the
 *  full-page and popup row types). */
export interface AuditTimelineRow {
  oid:            string;
  createdDate?:   string | null;
  createdBy?:     string | null;
  operationType?: string | null;
  formName?:      string | null;
  recordId?:      string | null;
  propertyName?:  string | null;
  oldValue?:      string | null;
  newValue?:      string | null;
  /** The free-text reason / note written with the change (AuditData.Comment),
   *  shown by the `record` variant under the values (v68 `.rsn`). */
  comment?:       string | null;
}

/** Operation → colour variant.  Drives BOTH the grid Action pill and the
 *  Timeline: created green, value-changed blue, deleted red. */
export function opVariant(op?: string | null): 'ok' | 'warn' | 'alert' | 'info' {
  const k = (op ?? '').toLowerCase();
  if (k.includes('add') || k.includes('insert') || k.includes('creat')) return 'ok';
  if (k.includes('update') || k.includes('edit') || k.includes('chang')) return 'info';
  if (k.includes('delete') || k.includes('remove')) return 'alert';
  return 'info';
}

type OpColor = { dot: string; bg: string; text: string };
// token-exempt: chart — operation-coded series colours (created green · value-changed
// blue · deleted red · amber) with no matching --ai-* token.
const OP_INFO: OpColor = { dot: '#185FA5', bg: '#E6F1FB', text: '#0C447C' }; // Value changed — blue
export const OP_COLORS: Record<string, OpColor> = {
  ok:    { dot: '#3B6D11', bg: '#EAF3DE', text: '#27500A' }, // Created — green
  info:  OP_INFO,
  alert: { dot: '#A32D2D', bg: '#FCEBEB', text: '#791F1F' }, // Deleted — red
  warn:  { dot: '#854F0B', bg: '#FAEEDA', text: '#633806' }, // amber (legacy/other)
};

/** Initials for the "Changed By" avatar (e.g. "Service" → "SE", "E174807" → "E1"). */
function initials(name?: string | null): string {
  const s = (name ?? '').trim();
  if (!s) return '—';
  const p = s.split(/\s+/);
  const a = p[0] ?? '';
  const b = p.length > 1 ? (p[1] ?? '') : '';
  return (b ? (a[0] ?? '') + (b[0] ?? '') : s.slice(0, 2)).toUpperCase();
}

/** HH:mm from the shared datetime helper (no inline date formatting — §18.6). */
function timeOnly(iso?: string | null): string {
  const s = iso ? (fmtLocalDateTime(iso) || '') : '';
  const parts = s.split(' ');
  return parts.length > 1 ? (parts[parts.length - 1] ?? s) : s;
}

/** The `record` variant's operation colours (prototype v68 .tlev / .aop): a
 *  value change is ORANGE on the spine with an AMBER pill, a creation BLUE, a
 *  deletion RED. Token-exempt for the same reason as OP_COLORS — an
 *  operation-coded series with no matching --ai-* token; the amber is
 *  OP_COLORS.warn's and the blue OP_INFO's, so the two variants share a palette. */
const REC_TONE: Record<'changed' | 'created' | 'deleted', OpColor> = {
  // v68's own values (--or / --or-t / --or-ink · --info · --crit).
  changed: { dot: 'var(--ai-orange, #e8670a)', bg: '#fff1e7', text: '#a1500a' },
  created: { dot: '#2b5fd9', bg: '#ecf1fe', text: '#2b5fd9' },
  deleted: { dot: '#c0392b', bg: '#fbeceb', text: '#c0392b' },
};

/** The v68 tone for one operation — shared by the `record` variant and by the
 *  default layout's `palette="v68"`, so both read the same colours. */
function recTone(op?: string | null): OpColor {
  const v = opVariant(op);
  return v === 'ok' ? REC_TONE.created : v === 'alert' ? REC_TONE.deleted : REC_TONE.changed;
}

/* The `record` variant's rules, rendered with it (the component carries its own
   styles, as the default variant does). */
const REC_CSS = `
  .atl-rec{position:relative;padding:2px 4px 2px 28px;max-height:52vh;overflow-y:auto;container-type:inline-size}
  .atl-rec:before{content:'';position:absolute;left:7px;top:8px;bottom:8px;width:2px;background:var(--ai-line)}
  .atl-rec-ev{position:relative;margin-bottom:20px}
  .atl-rec-ev:last-child{margin-bottom:2px}
  .atl-rec-dot{position:absolute;left:-28px;top:1px;width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 1px var(--ai-line);box-sizing:border-box}
  .atl-rec-hd{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:9px}
  .atl-rec-when{font-size:13.5px;font-weight:700;color:var(--ai-ink)}
  .atl-rec-op{display:inline-flex;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px}
  /* token-exempt: v68's --faint (#93a0ad) — the project has no fainter-than-muted token. */
  .atl-rec-who{font-size:12px;color:#93a0ad}
  .atl-rec-tbl{background:#fbfcfd;border:1px solid var(--ai-line);border-radius:9px;overflow:hidden}
  .atl-rec-row{padding:10px 14px;border-bottom:1px solid var(--ai-line);font-size:13px}
  .atl-rec-row:last-child{border-bottom:none}
  .atl-rec-prop{display:block;font-weight:600;color:var(--ai-muted)}
  .atl-rec-val{display:block;margin-top:3px;color:var(--ai-ink);word-break:break-word}
  /* token-exempt: chart — v68's old/new diff colours (--crit / --ok). */
  .atl-rec-old{color:#c0392b;text-decoration:line-through}
  .atl-rec-new{color:#1c7a45;font-weight:600}
  .atl-rec-ar{color:#93a0ad;margin:0 7px}
  .atl-rec-em{color:#93a0ad;font-style:italic}
  .atl-rec-rsn{display:block;margin-top:3px;font-size:11px;color:#93a0ad;font-style:italic}
  /* Wide enough (a full page, not a rail): the field BESIDE its values in
     v68's 168px column. Narrow (a 300px rail): stacked, so nothing clips. */
  @container (min-width:560px){
    .atl-rec-row{display:grid;grid-template-columns:168px minmax(0,1fr);gap:14px;align-items:start}
    .atl-rec-val{margin-top:0}
  }
`;

interface Props {
  rows: AuditTimelineRow[];
  loading?: boolean;
  emptyText?: string;
  /**
   * `timeline` (default) — the day-grouped roadmap the Audit Trail Log and every
   * toolbar audit popup use. Unchanged.
   *
   * `record` — for a record's OWN history in a side rail (prototype v68): one
   * entry per SAVE — the rows written together by one person at one minute —
   * each headed by its full date-time, the operation as a pill and who, over a
   * bordered list of the fields that changed, old struck through → new. The
   * field sits ABOVE its values rather than beside them: a 300px rail cannot
   * hold both on one line, and v68's single row clips the value. Opt-in.
   */
  variant?: 'timeline' | 'record';
  /**
   * COLOURS only — the layout is untouched. `v68` recolours the default layout
   * to prototype v68's audit palette: orange spine dot + amber pill for a value
   * change (blue created, red deleted), and the old / new values as red
   * struck-through / green text instead of tinted chips. Opt-in (Service
   * Tracking's Audit card); every other audit view keeps the default colours.
   */
  palette?: 'default' | 'v68';
}

export function AuditTimeline({
  rows, loading, emptyText = 'No audit entries.', variant = 'timeline', palette = 'default',
}: Props) {
  /* `record`: consecutive rows with the same minute, user and operation are ONE
     save — which is how a person made them, and how v68 draws them. */
  const saves = useMemo(() => {
    const out: { key: string; when: string; who: string; op: string; items: AuditTimelineRow[] }[] = [];
    for (const r of rows) {
      const when = r.createdDate ? (fmtLocalDateTime(r.createdDate) || '—') : '—';
      const who  = r.createdBy || '—';
      const op   = r.operationType ?? '—';
      const last = out[out.length - 1];
      if (last && last.when === when && last.who === who && last.op === op) last.items.push(r);
      else out.push({ key: r.oid, when, who, op, items: [r] });
    }
    return out;
  }, [rows]);

  const groups = useMemo(() => {
    const map = new Map<string, AuditTimelineRow[]>();
    for (const r of rows) {
      const day = r.createdDate ? (fmtLocalDate(r.createdDate) || '—') : '—';
      const arr = map.get(day) ?? [];
      if (arr.length === 0) map.set(day, arr);
      arr.push(r);
    }
    return Array.from(map.entries()).map(([day, items]) => ({ day, items }));
  }, [rows]);

  if (variant === 'record') {
    return (
      <div className="atl-rec">
        {loading ? (
          <div className="bl-empty">Loading…</div>
        ) : saves.length === 0 ? (
          <div className="bl-empty">{emptyText}</div>
        ) : saves.map(sv => {
          const tone = recTone(sv.op);
          return (
            <div className="atl-rec-ev" key={sv.key}>
              <span className="atl-rec-dot" style={{ background: tone.dot }} />
              <div className="atl-rec-hd">
                <span className="atl-rec-when">{sv.when}</span>
                <span className="atl-rec-op" style={{ background: tone.bg, color: tone.text }}>
                  {/* As v68 (and XAF's enum) spells it: "ValueChanged". */}
                  {sv.op.replace(/\s+/g, '')}
                </span>
                <span className="atl-rec-who">by {sv.who}</span>
              </div>
              <div className="atl-rec-tbl">
                {sv.items.map((r, i) => (
                  <div className="atl-rec-row" key={`${r.oid}-${i}`}>
                    <span className="atl-rec-prop">{r.propertyName || '—'}</span>
                    <div className="atl-rec-vcell">
                      <span className="atl-rec-val">
                        {r.oldValue ? <span className="atl-rec-old" title={r.oldValue}>{r.oldValue}</span>
                                    : <span className="atl-rec-em">empty</span>}
                        <span className="atl-rec-ar">→</span>
                        {r.newValue ? <span className="atl-rec-new" title={r.newValue}>{r.newValue}</span>
                                    : <span className="atl-rec-em">empty</span>}
                      </span>
                      {/* Why it changed, when the writer said (v68 `.rsn`). */}
                      {r.comment && <span className="atl-rec-rsn">{r.comment}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        <style>{REC_CSS}</style>
      </div>
    );
  }

  return (
    <div className={`atl-tl${palette === 'v68' ? ' atl-pal-v68' : ''}`}>
      {loading ? (
        <div className="bl-empty">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="bl-empty">{emptyText}</div>
      ) : groups.map(g => (
        <div key={g.day} className="atl-grp">
          <div className="atl-day">{g.day}</div>
          <div className="atl-track">
            {g.items.map((r, i) => {
              const c = palette === 'v68'
                ? recTone(r.operationType)
                : (OP_COLORS[opVariant(r.operationType)] ?? OP_INFO);
              return (
                <div className="atl-entry" key={`${r.oid}-${i}`}>
                  <span className="atl-node" style={{ background: c.dot }} />
                  <div className="atl-card">
                    <div className="atl-head">
                      <span className="atl-pill" style={{ background: c.bg, color: c.text }}>
                        {r.operationType ?? '—'}
                      </span>
                      {r.propertyName && <span className="atl-field">{r.propertyName}</span>}
                      {r.recordId && <span className="atl-rec">· {r.recordId}</span>}
                      <span style={{ flex: 1 }} />
                      <span className="atl-time">{timeOnly(r.createdDate)}</span>
                    </div>
                    {(r.oldValue || r.newValue) && (
                      <div className="atl-diff">
                        {r.oldValue && <span className="atl-old" title={r.oldValue}>{r.oldValue}</span>}
                        {r.oldValue && r.newValue && <i className="ti ti-arrow-right atl-arr" aria-hidden="true" />}
                        {r.newValue && <span className="atl-new" title={r.newValue}>{r.newValue}</span>}
                      </div>
                    )}
                    <div className="atl-who">
                      <span className="atl-av">{initials(r.createdBy)}</span>
                      <span>{r.createdBy || '—'}{r.formName ? ' · ' + r.formName : ''}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <style>{`
        .atl-tl{padding:14px 16px;overflow-y:auto;flex:1}
        .atl-day{font-size:12px;font-weight:600;color:var(--ai-muted);margin:8px 0 8px}
        .atl-track{position:relative;padding-left:26px}
        .atl-track:before{content:'';position:absolute;left:9px;top:4px;bottom:4px;width:2px;background:var(--ai-line)}
        .atl-entry{position:relative;margin-bottom:12px}
        .atl-node{position:absolute;left:-22px;top:13px;width:13px;height:13px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px var(--ai-line)}
        .atl-card{background:#fff;border:1px solid var(--ai-line-soft);border-radius:8px;padding:10px 12px}
        .atl-head{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .atl-pill{font-size:11px;font-weight:600;padding:2px 8px;border-radius:6px}
        .atl-field{font-size:13px;font-weight:600;color:var(--ai-ink)}
        .atl-rec{font-size:11px;color:var(--ai-muted)}
        .atl-time{font-size:12px;color:var(--ai-muted)}
        .atl-diff{margin:8px 0;font-size:13px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        /* token-exempt: chart — old/new diff + avatar use the audit series colours (match OP_COLORS). */
        .atl-old{background:#FCEBEB;color:#791F1F;text-decoration:line-through;padding:1px 6px;border-radius:4px;max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .atl-new{background:#EAF3DE;color:#27500A;padding:1px 6px;border-radius:4px;max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .atl-arr{color:var(--ai-muted)}
        .atl-who{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--ai-muted);margin-top:2px}
        .atl-av{width:20px;height:20px;border-radius:50%;background:#e6f1fb;color:#0c447c;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:600;flex:none}
        /* palette="v68": COLOUR ONLY — red / green text on no tint. Padding, weight and wrapping stay the
           default's, so the layout cannot move (stripping the padding once shortened a card by 29px). */
        .atl-pal-v68 .atl-old{background:none;color:#c0392b}
        .atl-pal-v68 .atl-new{background:none;color:#1c7a45}
      `}</style>
    </div>
  );
}
